import type { Delta } from 'quill/core';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { matchPath, useLocation } from 'react-router';
import AiCopilotChatPanel, { useConversationData } from './AiCopilotChatPanel';
import useAiCopilotEnabled from './useAiCopilotEnabled';
import { useRightPanel, useRightPanelWidth } from '../app/layout/RightPanel';
import { isFeatureEnabled } from '../utils/features';
import useShortcut from '../utils/useShortcut';

const ToggleAiCopilotChatPanelContext = createContext<(() => void) | null>(
  null,
);

export const useToggleAiCopilotChatPanel = () =>
  useContext(ToggleAiCopilotChatPanelContext);

const StartAiCopilotConversationContext = createContext<
  ((message: Delta) => void) | null
>(null);

export const useStartAiCopilotConversation = () =>
  useContext(StartAiCopilotConversationContext);

const DISALLOWED_PATHS = ['/admin/*', '/settings/*'];

interface AiCopilotProps {
  children: React.ReactNode;
}

const AiCopilotProvider = !isFeatureEnabled('ai_ask')
  ? ({ children }: AiCopilotProps) => children
  : ({ children }: AiCopilotProps) => {
      const { openRightPanel, isRightPanelOpen, closeRightPanel } =
        useRightPanel();

      const width = useRightPanelWidth();
      const lastWidthRef = useRef<number | null>(null);

      useLayoutEffect(() => {
        if (isRightPanelOpen) {
          lastWidthRef.current = width;
        }
      }, [width, isRightPanelOpen]);

      const location = useLocation();
      const previousPathnameRef = useRef(location.pathname);

      useEffect(() => {
        if (
          !location.state?.keepCopilotOpen &&
          previousPathnameRef.current !== location.pathname
        ) {
          closeRightPanel();
        }
        previousPathnameRef.current = location.pathname;
      }, [location.state, location.pathname, closeRightPanel]);

      const isCopilotDisallowed = useMemo(
        () =>
          DISALLOWED_PATHS.some((path) => matchPath(path, location.pathname)),
        [location.pathname],
      );

      const isAiCopilotEnabled = useAiCopilotEnabled();
      const isChatPanelOpenRef = useRef(false);

      useLayoutEffect(() => {
        if (!isRightPanelOpen) {
          isChatPanelOpenRef.current = false;
        }
      }, [isRightPanelOpen]);

      useEffect(() => {
        if (isCopilotDisallowed) {
          closeRightPanel();
        }
      }, [isCopilotDisallowed, closeRightPanel]);

      const toggleChatPanel = useCallback(() => {
        if (isCopilotDisallowed) {
          return;
        }

        if (!isChatPanelOpenRef.current) {
          openRightPanel(<AiCopilotChatPanel />, {
            isResizable: true,
            initialWidth: lastWidthRef.current ?? undefined,
          });
        } else {
          closeRightPanel();
        }
        isChatPanelOpenRef.current = !isChatPanelOpenRef.current;
      }, [
        isChatPanelOpenRef,
        openRightPanel,
        closeRightPanel,
        isCopilotDisallowed,
      ]);

      const { startNewConversation } = useConversationData();

      const startAiCopilotConversation = useCallback(
        (message: Delta) => {
          if (isCopilotDisallowed) {
            return;
          }

          startNewConversation(message);
          openRightPanel(<AiCopilotChatPanel />, {
            isResizable: true,
            initialWidth: lastWidthRef.current ?? undefined,
          });

          isChatPanelOpenRef.current = true;
        },
        [isCopilotDisallowed, openRightPanel, startNewConversation],
      );

      useShortcut(
        'mod+option+b',
        (e) => {
          e.preventDefault();
          toggleChatPanel();
        },
        {
          skip: !isAiCopilotEnabled,
          global: true,
        },
      );

      return (
        <StartAiCopilotConversationContext.Provider
          value={isAiCopilotEnabled ? startAiCopilotConversation : null}
        >
          <ToggleAiCopilotChatPanelContext.Provider
            value={isAiCopilotEnabled ? toggleChatPanel : null}
          >
            {children}
          </ToggleAiCopilotChatPanelContext.Provider>
        </StartAiCopilotConversationContext.Provider>
      );
    };

export default AiCopilotProvider;
